import Dexie from 'dexie';

import { logError, logFatal, logInfo } from './logger';
import { currentUserId, isFeatureFlagEnabled } from './meta';
import { computeResourceFingerprint } from './resourceFingerprint';
import { purgeStaleDeadUploads } from './stale_upload_purge';

// enable dexie debug if debug is enabled
Dexie.debug = isFeatureFlagEnabled('debug');

export const DEXIE_IS_LOADING = Symbol('DEXIE_IS_LOADING');

// namespace the db to currentUserId so the data is clearly segmented.
// the anon fallback is only for the rare/unsupported case the javascript runs without a user
const db = new Dexie(`output:v4:${currentUserId() || 'anon'}`);

// NOTE: Don't declare all columns like in SQL, only declare properties you want to index (ie properties you want to use in a where(…) query)
// When adding/modifying stores: bump the version number and adjust the existing `stores{}` block below. DO NOT add new version blocks for each version.
db.version(12).stores({
  // client-side only
  _catchup_envelopes: '++id, status',
  _fingerprints: 'key',
  _pending_upload_blobs: '[workspace_id+upload_id], workspace_id',
  _resource_coverage: '[workspace_id+resource], workspace_id, resource, status',
  widget_runtime_titles: '[workspace_id+widget_id], workspace_id, widget_id',
  workspace_latest_messages: 'id, [workspace_id+sort_order], workspace_id',

  // server-side, scoped by workspace
  agent_chats: '[workspace_id+id], [workspace_id+sort_order], workspace_id, kind',
  activities:
    'id, recipient_user_id, [recipient_user_id+sort_order], [recipient_user_id+read_at], [workspace_id+recipient_id+sort_order], [workspace_id+sort_order], workspace_id',
  board_items: '[workspace_id+id], [workspace_id+boardable_type], [workspace_id+pinned+sort_order], workspace_id',
  channels: '[workspace_id+id], [workspace_id+kind], workspace_id',
  channel_read_markers: '[workspace_id+id], [workspace_id+channel_id], workspace_id',
  emojis: '[workspace_id+id], [workspace_id+sort_order], workspace_id',
  mentions:
    '[workspace_id+id], [workspace_id+sort_order], [workspace_id+mentionable_id], [workspace_id+source_id+recipient_id+sort_order], [workspace_id+pseudotype+sort_order]',
  profiles: '[workspace_id+id], [workspace_id+role], workspace_id',
  widgets: '[workspace_id+id], [workspace_id+agent_chat_id], [workspace_id+status], workspace_id',

  // server-side, scoped by channel
  // [channel_id+type+sort_order] backs the cold-start transcript fallback: /messages/bootstrap
  // seeds only this table, and background replication skips channels with no feed sync cursor.
  messages: 'id, [channel_id+sort_order], [pseudotype+sort_order], [channel_id+type+sort_order], [workspace_id+sort_order], workspace_id',
  message_coverage_intervals:
    'id, channel_id, [channel_id+lower_sort_order], [channel_id+upper_sort_order], [channel_id+fetched_at], workspace_id',
  // [channel_id+type+sort_order] backs latest-by-type point queries (e.g. newest
  // conference transcript); the compound index is sparse since most rows have no `type`.
  feed_messages: 'id, [channel_id+sort_order], [channel_id+type+sort_order], [workspace_id+sort_order]',
  feed_coverage: 'id, channel_id, [channel_id+lower_sort_order], [channel_id+upper_sort_order]',
  message_feed_meta: '[workspace_id+channel_id]',

  // server-side, unscoped
  direct_messages: 'id, updated_at, workspace_id, main_channel_id, last_message_sort_order',
  media_attachments: 'id, workspace_id, session_key, channel_id, attachment_id, written_at',
  users: 'id',
  workspaces: 'id, slug',
});

// add event listeners
db.on('ready', async function (vipDb) {
  logInfo('dexie', 'Database ready');

  try {
    const result = await purgeStaleDeadUploads(vipDb);
    if (result.didRun) logInfo('dexie', 'Stale dead uploads purged', result);
  } catch (error) {
    // Keep the database available if maintenance fails. Since the completion marker
    // is written in the same transaction, the cleanup will be retried next load.
    logError('dexie', 'Failed to purge stale dead uploads', error);
  }
});

db.on('blocked', function () {
  logFatal('dexie', 'Database upgrading was blocked by another window');
});

db.on('close', function () {
  logFatal('dexie', 'Database was closed');
});

db.open().catch(function (err) {
  logFatal('dexie', 'Failed to open db in init', err);
});

// Fingerprint utilities — compute a SHA-256 hash of sorted IDs (plus additional fields when available)
// to detect whether a dataset has changed since the last bulkPut.
// This lets us skip redundant writes that trigger useLiveQuery cascades.
export async function hasDataChanged(key, items) {
  try {
    const hash = await computeResourceFingerprint(key, items);
    const existing = await db._fingerprints.get(key);
    const changed = !existing || existing.hash !== hash;
    return { changed, hash };
  } catch {
    return { changed: true, hash: null };
  }
}

export async function storeFingerprint(key, hash) {
  try {
    await db._fingerprints.put({ key, hash, updatedAt: Date.now() });
  } catch {
    // noop just to be safe
  }
}

export async function clearFingerprint(key) {
  try {
    await db._fingerprints.delete(key);
  } catch {
    // noop just to be safe
  }
}

export default db;
